import { Button, Stack, Text } from '@chakra-ui/react'
import { useCallback, useMemo } from 'react'

import { COURSE_SLUG_SAT_BOOTCAMP_MATH } from '@/queries/constants'
import { RPCRefresh } from '@/client/rpc'
import * as url from '@/client/services/url'

import ShwLink from '@/components/ShwLink'
import { useToaster } from '@/components/Toast/useToaster'
import Form from '@/components/UI/Form/Form'
import type { FormInputType } from '@/components/UI/Form/types'
import ModalV3 from '@/components/UI/Modal/ModalV3'

export default function CompleteLessonPrepModal({
  sessionId,
  open,
  onClose,
  courseSlug,
  sessionIndexInSeries,
}: {
  sessionId: number
  open: boolean
  onClose: () => void
  courseSlug: string
  sessionIndexInSeries: number
}): JSX.Element {
  const toast = useToaster()

  const markCompleteFormInputs: FormInputType[] = useMemo(
    () => [
      {
        type: 'multi-checkbox',
        name: 'mark_complete',
        label: 'Mark Lesson Prep Complete',
        options: [
          {
            value: 'certifyCompleted',
            label: 'I certify that I have completed my lesson prep for this session.',
          },
        ],
        required: true,
        size: 6,
      },
    ],
    []
  )

  const handleMarkLessonPrepComplete = useCallback(async () => {
    await RPCRefresh.markSATLessonPrepCompletion({
      sessionId,
      completed: true,
    })

    onClose()
    toast({
      title: 'Successfully marked lesson prep complete',
      status: 'success',
    })
  }, [onClose, sessionId, toast])

  return (
    <ModalV3
      size="xl"
      open={open}
      onClose={onClose}
      title={`Prepare for Session ${sessionIndexInSeries} of your bootcamp`}
      body={() => (
        <Stack direction="column" spacing={6}>
          <Text>
            Before <b>Session {sessionIndexInSeries}</b> of your{' '}
            <b>
              {courseSlug === COURSE_SLUG_SAT_BOOTCAMP_MATH ? 'Math' : 'Reading & Writing'} Bootcamp
            </b>
            , make sure to review the lesson prep in the{' '}
            <ShwLink href={url.getSATBootcampCurriculumUrl()}>Curriculum</ShwLink>. Once you do,
            please mark this task as completed.
          </Text>
          <Form
            name="MarkLessonPrepComplete"
            blocks={markCompleteFormInputs}
            onSubmit={handleMarkLessonPrepComplete}
            FooterComponent={({ submitting }) => (
              <Stack justify="flex-end" direction="row" spacing="8px" mt="16px">
                <Button variant="outline" onClick={onClose}>
                  Close
                </Button>
                <Button isLoading={submitting} type="submit">
                  Submit
                </Button>
              </Stack>
            )}
          />
        </Stack>
      )}
    />
  )
}
